import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";

import { ErrorFallback } from "./error-fallback";

declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
    };
  }
}

interface AppError extends Error {
  code: string;
  details?: Record<string, unknown>;
  timestamp?: string;
}

interface GlobalErrorBoundaryProps {
  children: ReactNode;
}

interface GlobalErrorBoundaryState {
  error: unknown;
  eventId: string | undefined;
}

/**
 * Root error boundary for the widget host. DevTools dispatches `simulate-global-error`
 * which is applied via setState (not a render-phase throw) so React 19 does not recover
 * the root instead of showing this fallback.
 */
export class GlobalErrorBoundary extends Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  override state: GlobalErrorBoundaryState = {
    error: null,
    eventId: undefined,
  };

  static getDerivedStateFromError(error: unknown): Partial<GlobalErrorBoundaryState> {
    return { error };
  }

  override componentDidMount() {
    window.addEventListener("simulate-global-error", this.handleSimulatedGlobalError);
  }

  override componentWillUnmount() {
    window.removeEventListener("simulate-global-error", this.handleSimulatedGlobalError);
  }

  handleSimulatedGlobalError = (event: Event) => {
    const { error } = (event as CustomEvent<{ error: Error }>).detail;
    this.setState({ error, eventId: undefined });
    this.reportError(error, {
      componentStack: "\n    in GlobalErrorBoundary (simulated)",
    });
  };

  override componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    this.reportError(error, errorInfo);
  }

  reportError(error: unknown, errorInfo: ErrorInfo) {
    Sentry.withScope((scope) => {
      scope.setTag("errorBoundary", "GlobalErrorBoundary");
      scope.setLevel("error");
      scope.setContext("errorInfo", {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        stackTrace: error instanceof Error ? error.stack : undefined,
      });

      if (error instanceof Error && "code" in error) {
        const appError = error as AppError;
        scope.setTag("errorCode", appError.code);
        scope.setContext("appErrorDetails", appError.details || {});
      }

      const eventId = Sentry.captureReactException(error, errorInfo, {
        mechanism: { handled: true, type: "auto.function.react.error_boundary" },
      });

      this.setState({ eventId });

      if (import.meta.env.DEV) {
        console.error("🚨 Global Error Boundary:", { error, errorInfo });
      }

      if (typeof window !== "undefined" && window.posthog) {
        window.posthog.capture("error_boundary_triggered", {
          error_message: error instanceof Error ? error.message : String(error),
          error_stack: error instanceof Error ? error.stack : undefined,
          component_stack: errorInfo.componentStack,
          url: window.location.href,
        });
      }
    });
  }

  resetErrorBoundary = () => {
    this.setState({ error: null, eventId: undefined });
  };

  override render() {
    if (this.state.error != null) {
      return (
        <ErrorFallback
          error={this.state.error}
          resetError={this.resetErrorBoundary}
          eventId={this.state.eventId}
          source="global"
        />
      );
    }

    return this.props.children;
  }
}
